import React, { useState, useEffect } from 'react';

import {
  Container,
  Typography,
  Button,
  Box,
  TextField,
  FormControl,
  MenuItem,
  InputLabel,
  CircularProgress,
  Select
} from '@mui/material';

import {
  useParams,
  useLocation,
  useNavigate
} from 'react-router-dom';

import fascicoloApi from '../../api/fascicoloApi';
import annoFiscaleApi from '../../api/annoFiscaleApi';
import praticaApi from '../../api/praticaApi';

import BackButtonDesktop from '../../components/BackButtonDesktop';

const tipiDocumento = [
  'DOCUMENTO_IDENTITA',
  'CONTRATTO',
  'UNICO',
  'MULTA',
  'PAGOPA',
  'ATTO_SOCIETARIO',
  'BANCARIO',
  'RATEIZZAZIONE_ADE',
  'ROTTAMAZIONE',
  'AMA',
  'ACCERTAMENTO_TARI',
  'F24',
  'FF24PREDETERMINATO',
  'TARI',
  'INTIMAZIONEPAG_AGRISC',
  'BOLLOAUTO',
  'INARCASSA',
  'FATTURA_PASSIVA',
  'FATTURA_ATTIVA',
  'ALTRO'
];

export default function CreateDocumentoFascicoloPage() {

  const { fascicoloId } = useParams();

  const location = useLocation();

  const navigate = useNavigate();

  const {
    richiestaId,
    tipo,
    contribuenteId,
    annoFiscaleId: initialAnnoFiscaleId,
    praticaId: initialPraticaId
  } = location.state || {};

  const [loading, setLoading] = useState(false);

  const [file, setFile] = useState(null);

  const [descrizione, setDescrizione] = useState('');

  const [versione, setVersione] = useState('');

  const [tipoDocumento, setTipoDocumento] = useState(tipo || '');

  const [anniFiscali, setAnniFiscali] = useState([]);

  const [pratiche, setPratiche] = useState([]);

  const [annoFiscaleId, setAnnoFiscaleId] = useState(
    initialAnnoFiscaleId || ''
  );

  const [praticaId, setPraticaId] = useState(
    initialPraticaId || ''
  );

  // =========================
  // LOAD ANNI FISCALI
  // =========================

  useEffect(() => {

    const load = async () => {

      try {

        const res = await annoFiscaleApi.getByContribuente(contribuenteId);

        setAnniFiscali(res.data || []);

      } catch (err) {

        console.error(err);

      }

    };

    if (contribuenteId) {
      load();
    }

  }, [contribuenteId]);

  // =========================
  // LOAD PRATICHE
  // =========================

  useEffect(() => {

    const loadPratiche = async () => {

      if (!annoFiscaleId) return;

      try {

        const res = await praticaApi.getByAnnoFiscale(annoFiscaleId);

        setPratiche(res.data || []);

      } catch (err) {

        console.error(err);

      }

    };

    loadPratiche();

  }, [annoFiscaleId]);

  // =========================
  // CHANGE ANNO
  // =========================

  const handleAnnoChange = (id) => {

    setAnnoFiscaleId(id);

    setPraticaId('');

  };

  // =========================
  // FILE CHANGE
  // =========================

  const handleFileChange = (e) => {

    if (e.target.files && e.target.files.length > 0) {

      setFile(e.target.files[0]);

    }

  };

  // =========================
  // UPLOAD
  // =========================

  const handleUpload = async () => {

    if (!file) {
      return;
    }

    try {

      setLoading(true);

      const formData = new FormData();

      formData.append('file', file);

      formData.append('descrizione', descrizione);

      formData.append('versione', versione);

      formData.append('tipoDocumento', tipoDocumento);

      if (annoFiscaleId) {
        formData.append('annoFiscaleId', annoFiscaleId);
      }

      if (praticaId) {
        formData.append('praticaId', praticaId);
      }

      if (richiestaId) {
        formData.append('richiestaId', richiestaId);
      }

      await fascicoloApi.uploadDocumento(
        fascicoloId,
        formData
      );

      navigate(-1);

    } catch (err) {

      console.error(err);

    } finally {

      setLoading(false);

    }

  };

  return (

    <Container maxWidth="md">

      {loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}

      <BackButtonDesktop />

      <Typography
        variant="h5"
        sx={{ mb: 3 }}
      >
        Carica Documento nel Fascicolo
      </Typography>

      {/* ANNO FISCALE */}

      <FormControl
        fullWidth
        sx={{ mb: 2 }}
      >

        <InputLabel>
          Anno fiscale
        </InputLabel>

        <Select
          value={annoFiscaleId}
          label="Anno fiscale"
          onChange={(e) =>
            handleAnnoChange(e.target.value)
          }
        >

          {anniFiscali.map((a) => (

            <MenuItem
              key={a.id}
              value={a.id}
            >
              {a.anno}
            </MenuItem>

          ))}

        </Select>

      </FormControl>

      {/* PRATICA */}

      <FormControl
        fullWidth
        sx={{ mb: 2 }}
        disabled={!annoFiscaleId || !!initialPraticaId}
      >

        <InputLabel>
          Pratica
        </InputLabel>

        <Select
          value={praticaId}
          label="Pratica"
          onChange={(e) =>
            setPraticaId(e.target.value)
          }
        >

          {pratiche.map((p) => (

            <MenuItem
              key={p.id}
              value={p.id}
            >
              {p.nome}
            </MenuItem>

          ))}

        </Select>

      </FormControl>

      {/* TIPO DOCUMENTO */}

      <FormControl
        fullWidth
        sx={{ mb: 2 }}
      >

        <InputLabel>
          Tipo Documento
        </InputLabel>

        <Select
          value={tipoDocumento}
          label="Tipo Documento"
          onChange={(e) =>
            setTipoDocumento(e.target.value)
          }
        >

          {tipiDocumento.map((t) => (

            <MenuItem
              key={t}
              value={t}
            >
              {t}
            </MenuItem>

          ))}

        </Select>

      </FormControl>

      {/* DESCRIZIONE */}

      <TextField
        label="Descrizione Documento"
        value={descrizione}
        onChange={(e) =>
          setDescrizione(e.target.value)
        }
        fullWidth
        sx={{ mb: 2 }}
      />

      {/* VERSIONE */}

      <TextField
        label="Versione"
        type="number"
        value={versione}
        onChange={(e) =>
          setVersione(e.target.value)
        }
        fullWidth
        sx={{ mb: 2 }}
      />

      {/* FILE */}

      <Box sx={{ mt: 2 }}>

        <input
          accept="application/pdf"
          style={{ display: 'none' }}
          id="contained-button-file"
          type="file"
          onChange={handleFileChange}
        />

        <label htmlFor="contained-button-file">

          <Button
            variant="outlined"
            component="span"
          >
            {file ? 'Cambia PDF' : 'Sfoglia PDF'}
          </Button>

        </label>

        {file && (

          <Typography
            variant="body2"
            sx={{ mt: 1 }}
          >
            {file.name}
          </Typography>

        )}

      </Box>

      {/* SAVE */}

      <Button
        variant="contained"
        sx={{ mt: 3 }}
        onClick={handleUpload}
        disabled={!file || !tipoDocumento}
      >
        Carica Documento
      </Button>

    </Container>

  );

}